import React from 'react';
import { Button } from './ui/button';

/**
 * Composant de pagination réutilisable pour les tableaux
 * 
 * @param {number} currentPage - Page actuelle (1-indexed)
 * @param {number} totalItems - Nombre total d'éléments
 * @param {number} itemsPerPage - Nombre d'éléments par page
 * @param {function} onPageChange - Callback appelé lors du changement de page
 * @param {string} itemLabel - Label des éléments (ex: "opérations", "clients")
 */
const TablePagination = ({ 
  currentPage, 
  totalItems, 
  itemsPerPage, 
  onPageChange,
  itemLabel = "éléments"
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  // Don't render if no pagination needed
  if (totalItems <= itemsPerPage) return null;
  
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage + 1;
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, totalItems);
  
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    // First page
    if (startPage > 1) {
      pages.push(
        <Button
          key={1}
          variant={1 === currentPage ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(1)}
          className="w-8 h-8 p-0"
        >
          1
        </Button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis-start" className="px-2">...</span>);
      }
    }
    
    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === currentPage ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(i)}
          className="w-8 h-8 p-0"
        >
          {i}
        </Button>
      );
    }
    
    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis-end" className="px-2">...</span>);
      }
      pages.push(
        <Button
          key={totalPages}
          variant={totalPages === currentPage ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(totalPages)}
          className="w-8 h-8 p-0"
        >
          {totalPages}
        </Button>
      );
    }
    
    return pages;
  };
  
  return (
    <div className="mt-4 flex items-center justify-between border-t pt-4">
      <div className="text-sm text-gray-600">
        Affichage de {indexOfFirstItem} à {indexOfLastItem} sur {totalItems} {itemLabel}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Précédent
        </Button>
        <div className="flex items-center gap-1">
          {getPageNumbers()}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
        >
          Suivant
        </Button>
      </div>
    </div>
  );
};

export default TablePagination;
